// MarketDataContext.tsx - Shared market data context (consumes from primary WebSocket)
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWebSocket } from './WebSocketContext';

export interface MarketData {
  price: number;
  change: number;
  changePercent: number;
}

export interface LiveMarketDataResponse {
  timestamp: string;
  data: {
    NIFTY50: MarketData;
    BANKNIFTY: MarketData;
    SENSEX: MarketData;
  };
  status: string;
  frequency?: string;
}

// Zippy Format Interface
export interface ZippyMarketData {
  xID: number;    // Exchange Instrument ID
  LTP: number;    // Last Traded Price
  Time: number;   // Unix timestamp
  Change: number; // Percent Change
}

interface MarketDataContextType {
  marketData: LiveMarketDataResponse | null;
  isConnected: boolean;
  getInstrumentPrice: (instrument: 'NIFTY' | 'BANKNIFTY' | 'SENSEX') => number;
  updateMarketData: (data: LiveMarketDataResponse) => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastTickTime: string | null;
}

const MarketDataContext = createContext<MarketDataContextType | undefined>(undefined);

export const useSharedMarketData = () => {
  const context = useContext(MarketDataContext);
  if (context === undefined) {
    throw new Error('useSharedMarketData must be used within a MarketDataProvider');
  }
  return context;
};

interface MarketDataProviderProps {
  children: React.ReactNode;
}

// Dhan security ID → instrument name (index spot prices only)
const ZIPPY_INSTRUMENT_MAP: Record<number, string> = {
  13: 'NIFTY50',
  26000: 'NIFTY50',
  25: 'BANKNIFTY',
  26001: 'BANKNIFTY',
  51: 'SENSEX',
  26065: 'SENSEX',
};

export const resolveIndexMarketData = (instrumentId: number): string | undefined =>
  ZIPPY_INSTRUMENT_MAP[instrumentId];

const DEFAULT_MARKET: MarketData = { price: 0, change: 0, changePercent: 0 };

export const MarketDataProvider: React.FC<MarketDataProviderProps> = ({ children }) => {
  const { isConnected: wsConnected, onMarketData } = useWebSocket();

  const [currentMarketData, setCurrentMarketData] = useState<LiveMarketDataResponse | null>(null);
  const [lastTickTime, setLastTickTime] = useState<string | null>(null);

  // PERFORMANCE OPTIMIZATION: Use ref to access latest market data in callbacks
  const currentMarketDataRef = React.useRef<LiveMarketDataResponse | null>(null);
  const lastTickTimeRef = React.useRef<string | null>(null);

  // Derive connection status from primary WebSocket
  const connectionStatus: 'connected' | 'disconnected' = wsConnected ? 'connected' : 'disconnected';

  // Attach market data handler to the primary WebSocket's callback ref
  useEffect(() => {
    onMarketData.current = (message: any) => {
      try {
        // Handle new Zippy format (xID, LTP, Time, Change)
        if (message.type === 'zippy_market_data') {
          const { xID, LTP, Time, Change } = message.data;
          const instrumentName = resolveIndexMarketData(xID);

          if (instrumentName && LTP !== undefined) {
            if (xID === 13 && Time) {
              const t = String(Time);
              if (!lastTickTimeRef.current || t >= lastTickTimeRef.current) {
                lastTickTimeRef.current = t;
                setLastTickTime(t);
              }
            }

            const changePercent = Change || 0;
            const absoluteChange = (LTP * changePercent) / 100;

            setCurrentMarketData(prevData => {
              const newData: LiveMarketDataResponse = {
                timestamp: new Date().toISOString(),
                data: {
                  NIFTY50: instrumentName === 'NIFTY50'
                    ? { price: LTP, change: absoluteChange, changePercent }
                    : (prevData?.data?.NIFTY50 || DEFAULT_MARKET),
                  BANKNIFTY: instrumentName === 'BANKNIFTY'
                    ? { price: LTP, change: absoluteChange, changePercent }
                    : (prevData?.data?.BANKNIFTY || DEFAULT_MARKET),
                  SENSEX: instrumentName === 'SENSEX'
                    ? { price: LTP, change: absoluteChange, changePercent }
                    : (prevData?.data?.SENSEX || DEFAULT_MARKET),
                },
                status: 'connected',
              };
              currentMarketDataRef.current = newData;
              return newData;
            });
          }
        }

        // Handle legacy market_data format
        if (message.type === 'market_data') {
          const { instrument, ltp } = message.data;
          const instrumentName = resolveIndexMarketData(instrument);

          if (instrumentName && ltp !== undefined) {
            setCurrentMarketData(prevData => {
              const newData: LiveMarketDataResponse = {
                timestamp: new Date().toISOString(),
                data: {
                  NIFTY50: instrumentName === 'NIFTY50'
                    ? { price: ltp, change: 0, changePercent: 0 }
                    : (prevData?.data?.NIFTY50 || DEFAULT_MARKET),
                  BANKNIFTY: instrumentName === 'BANKNIFTY'
                    ? { price: ltp, change: 0, changePercent: 0 }
                    : (prevData?.data?.BANKNIFTY || DEFAULT_MARKET),
                  SENSEX: instrumentName === 'SENSEX'
                    ? { price: ltp, change: 0, changePercent: 0 }
                    : (prevData?.data?.SENSEX || DEFAULT_MARKET),
                },
                status: 'connected',
              };
              currentMarketDataRef.current = newData;
              return newData;
            });
          }
        }
      } catch (error) {
        console.error('⚠️ Error processing market data message:', error);
      }
    };

    return () => {
      onMarketData.current = null;
    };
  }, [onMarketData]);

  // Update function for live market data (external callers)
  const updateMarketData = useCallback((data: LiveMarketDataResponse) => {
    currentMarketDataRef.current = data;
    setCurrentMarketData(data);
  }, []);

  // PERFORMANCE OPTIMIZATION: Use ref to access current data without dependency
  const getInstrumentPrice = useCallback((instrument: 'NIFTY' | 'BANKNIFTY' | 'SENSEX'): number => {
    const data = currentMarketDataRef.current;
    switch (instrument) {
      case 'NIFTY':
        return data?.data.NIFTY50?.price || 0;
      case 'BANKNIFTY':
        return data?.data.BANKNIFTY?.price || 0;
      case 'SENSEX':
        return data?.data.SENSEX?.price || 0;
      default:
        return 0;
    }
  }, []);

  const marketData = currentMarketData;
  const isConnected = connectionStatus === 'connected';

  const contextValue = useMemo(() => ({
    marketData,
    isConnected,
    getInstrumentPrice,
    updateMarketData,
    connectionStatus,
    lastTickTime,
  }), [marketData, isConnected, getInstrumentPrice, updateMarketData, connectionStatus, lastTickTime]);

  return (
    <MarketDataContext.Provider value={contextValue}>
      {children}
    </MarketDataContext.Provider>
  );
};
